import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Link as LinkIcon, Building, MessageSquare, AlertCircle, Sofa, Camera, Check, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function OrderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isFirstOrder, setIsFirstOrder] = useState(true);

  const initialService = location.state?.service || "cliptour_staged";
  
  // ⚡ Récupère isB2B du state OU de localStorage (pour les retours Stripe)
  const isB2BFromState = location.state?.isB2B;
  const isB2BFromStorage = localStorage.getItem("cliptour_isB2B") === "true";
  const isB2B = isB2BFromState !== undefined ? isB2BFromState : isB2BFromStorage;

  // ⚡ Détermine où le logo doit rediriger
  const homePath = isB2B ? "/partners" : "/";

  // Check if user signed up via Google and has no company name saved
  const isGoogleUser = user?.auth_method === "google";
  const needsCompanyName = isGoogleUser && !user?.company_name;

  const [formData, setFormData] = useState({
    service_type: initialService,
    photo_link: "",
    company_name: "",
    notes: "",
    service_suggestion: "",
    marketing_consent: false
  });

  // ⚡ Stocke isB2B dans localStorage pour les redirections Stripe
  useEffect(() => {
    if (isB2BFromState !== undefined) {
      localStorage.setItem("cliptour_isB2B", isB2BFromState.toString());
      localStorage.setItem("cliptour_isB2B_timestamp", Date.now().toString());
    }
    
    // ⚡ Nettoie localStorage si trop vieux (>1 heure)
    const timestamp = localStorage.getItem("cliptour_isB2B_timestamp");
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 60 * 60 * 1000) { // 1 heure
        localStorage.removeItem("cliptour_isB2B");
        localStorage.removeItem("cliptour_isB2B_timestamp");
      }
    }
  }, [isB2BFromState]);

  // ⚡ FORCE SCROLL EN HAUT AU MONTAGE DE LA PAGE
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Pre-fill company name from user profile
  useEffect(() => {
    if (user?.company_name) {
      setFormData(prev => ({ ...prev, company_name: user.company_name }));
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { redirectTo: "/order", service: initialService, isB2B } });
    }
  }, [user, authLoading, navigate, initialService, isB2B]);

  // Check if user has previous orders
  useEffect(() => {
    const checkFirstOrder = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const orders = await response.json();
          // User is first order if they have no completed orders
          const completedOrders = orders.filter(o => o.status !== 'pending');
          setIsFirstOrder(completedOrders.length === 0);
        }
      } catch (error) {
        console.error("Error checking orders:", error);
      }
    };
    checkFirstOrder();
  }, [user]);

  const getPriceDisplay = () => {
    const prices = {
      cliptour: { regular: isB2B ? 300 : 450, first: isB2B ? 200 : 350 },
      cliptour_staged: { regular: isB2B ? 550 : 700, first: isB2B ? 450 : 600 }
    };
    const p = prices[formData.service_type];
    return { regular: p.regular, first: p.first };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.photo_link) {
      toast.error("Please provide a link to your property photos");
      return;
    }

    // Auto-prepend https:// if missing
    let photoLink = formData.photo_link.trim();
    if (photoLink && !photoLink.startsWith("http://") && !photoLink.startsWith("https://")) {
      photoLink = "https://" + photoLink;
    }

    if (!formData.company_name.trim()) {
      toast.error("Please provide your company name");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const originUrl = window.location.origin;

      // If Google user and company name was not saved, save it to profile
      if (needsCompanyName && formData.company_name.trim()) {
        await fetch(`${API}/auth/me`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          credentials: "include",
          body: JSON.stringify({ company_name: formData.company_name.trim() })
        });
        // Refresh user data to update context
        if (refreshUser) {
          refreshUser();
        }
      }

      const response = await fetch(`${API}/checkout/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        credentials: "include",
        body: JSON.stringify({
          service_type: formData.service_type,
          is_b2b: isB2B,
          origin_url: originUrl,
          order_data: {
            photo_link: photoLink,
            company_name: formData.company_name,
            notes: formData.notes,
            service_suggestion: formData.service_suggestion,
            marketing_consent: formData.marketing_consent
          }
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Save service type for payment success page
        localStorage.setItem("cliptour_last_service", formData.service_type);
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner w-8 h-8 border-slate-400" />
      </div>
    );
  }

  const price = getPriceDisplay();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="order-page">
      <Header homePath={homePath} />

      <section className="section-spacing pt-32">
        <div className="container-custom">
          <motion.div 
            className="max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">New Order</h1>
              <p className="text-slate-600">Fill in the details below to place your order</p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="order-form">
                {/* Service Type */}
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    Service Type *
                  </Label>
                  <div className="grid grid-cols-2 gap-3" data-testid="service-type-select">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, service_type: "cliptour" })}
                      className={`relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        formData.service_type === "cliptour"
                          ? "border-slate-900 bg-slate-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <Camera className={`w-6 h-6 ${formData.service_type === "cliptour" ? "text-slate-900" : "text-slate-400"}`} />
                      <span className={`text-sm font-semibold ${formData.service_type === "cliptour" ? "text-slate-900" : "text-slate-700"}`}>
                        Cliptour
                      </span>
                      <span className={`text-xs text-center ${formData.service_type === "cliptour" ? "text-slate-600" : "text-slate-400"}`}>
                        Already furnished
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, service_type: "cliptour_staged" })}
                      className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        formData.service_type === "cliptour_staged"
                          ? "border-slate-900 bg-slate-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      <Sofa className={`w-6 h-6 ${formData.service_type === "cliptour_staged" ? "text-slate-900" : "text-slate-400"}`} />
                      <span className={`text-sm font-semibold ${formData.service_type === "cliptour_staged" ? "text-slate-900" : "text-slate-700"}`}>
                        Cliptour Staged
                      </span>
                      <span className={`text-xs text-center ${formData.service_type === "cliptour_staged" ? "text-slate-600" : "text-slate-400"}`}>
                        Empty (needs staging)
                      </span>
                    </button>
                  </div>
                </div>

                {/* Price Display + Summary */}
                <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-slate-600 font-medium">
                      {formData.service_type === "cliptour_staged" ? "Cliptour Staged" : "Cliptour"}
                      {isB2B && " (B2B)"}
                    </span>
                    <div>
                      {isFirstOrder ? (
                        <>
                          <span className="text-slate-400 line-through text-sm">${price.regular}</span>
                          <span className="text-2xl font-bold text-slate-900 ml-2">${price.first}</span>
                          <span className="text-sm text-green-600 ml-2">First order!</span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-slate-900">${price.regular}</span>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                      <Clock className="w-4 h-4" />
                      <span>Delivery in <strong className="text-slate-700">{formData.service_type === "cliptour" ? "24" : "48"} business hours</strong></span>
                    </div>
                    <ul className="space-y-1.5">
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        10 video clips with lifestyle characters
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        1 full property tour with music
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        2 revisions included
                      </li>
                      {formData.service_type === "cliptour_staged" && (
                        <li className="flex items-center gap-2 text-sm text-[#3C59B2]">
                          <span className="w-3.5 h-3.5 flex items-center justify-center font-bold text-[#3C59B2] flex-shrink-0">+</span>
                          <span className="font-medium">10 photos with virtual furniture & decor</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                {/* Company Name */}
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    Company Name *
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Required for invoicing
                  </p>
                  {needsCompanyName && (
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-700">
                        Signed in via Google — please enter your company name. It will be saved for future orders.
                      </p>
                    </div>
                  )}
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="h-12 pl-10"
                      placeholder="Your company name"
                      data-testid="company-name-input"
                    />
                  </div>
                </div>

                {/* Photo Link */}
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    Property Photos *
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Share all your photos via any file sharing service — we'll select the best 10 for staging and animation
                  </p>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      required
                      value={formData.photo_link}
                      onChange={(e) => setFormData({ ...formData, photo_link: e.target.value })}
                      className="h-12 pl-10"
                      placeholder="Dropbox, Google Drive, WeTransfer..."
                      data-testid="photo-link-input"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    Notes (optional)
                  </Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="min-h-[100px]"
                    placeholder="Any special requests or preferences..."
                    data-testid="notes-input"
                  />
                </div>

                {/* Service Suggestion */}
                <div>
                  <Label className="text-slate-700 font-medium mb-2 block">
                    What other services would you like us to offer? (optional)
                  </Label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <Textarea
                      value={formData.service_suggestion}
                      onChange={(e) => setFormData({ ...formData, service_suggestion: e.target.value })}
                      className="min-h-[80px] pl-10"
                      placeholder="Branded intro, drone footage, outro card, template..."
                      data-testid="service-suggestion-input"
                    />
                  </div>
                </div>

                {/* Marketing Consent */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="marketing_consent"
                    checked={formData.marketing_consent}
                    onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                  />
                  <label htmlFor="marketing_consent" className="text-sm text-slate-600 cursor-pointer">
                    I agree that Cliptour may use the final videos for marketing and portfolio purposes
                  </label>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg font-semibold"
                  data-testid="proceed-checkout-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      Processing...
                    </span>
                  ) : (
                    `Proceed to Checkout — $${isFirstOrder ? price.first : price.regular}`
                  )}
                </Button>

                {isFirstOrder && (
                  <p className="text-center text-sm text-slate-500">
                    First-time customers get $100 off automatically applied at checkout
                  </p>
                )}

                <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                  <Shield className="w-3.5 h-3.5" />
                  <span>Your files are confidential and only used for production</span>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}